import { FormikProps, withFormik } from 'formik'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'

import { Body1 } from '@ui/Typography'
import { PopoverInput } from '@ui/components/Popover'

import { hasProductActivatedStatus } from '@utils/AccountAccess/accountProductStatuses'
import { FormSubmitValues } from '@utils/formValidation'

import { isFuturesValidationDenied } from '@model/AccountDetailedDto'
import { ClientLandingPageSettingDto } from '@model/LandingPageDto'
import { TickmillProductType } from '@model/TickmillProductType'

import { useScrollToTop } from '@hooks/useScrollToTop'

import { Box } from '@shared/components/Box'

export interface LandingPageFormValues {
  landingPageId?: number
}

interface OuterProps {
  landingPageOptions: ClientLandingPageSettingDto[]
  landingPage?: number
  onSubmit(values: FormSubmitValues<LandingPageFormValues>): Promise<void>
}

const LandingPageFormUI: FC<FormikProps<LandingPageFormValues> & OuterProps> = (props) => {
  useScrollToTop()
  const { landingPageOptions, setFieldValue } = props
  const { handleSubmit, values } = props
  const { account } = useAccountReadContext()
  const { t } = useTranslation()

  const isFuturesDenied = isFuturesValidationDenied(account)

  const activeProducts = useMemo(
    () => landingPageOptions.filter((x) => hasProductActivatedStatus(account, x.tickmillProductId)),
    [landingPageOptions, account]
  )

  const selectableProducts = useMemo(
    () =>
      activeProducts.filter(
        (p) => !(isFuturesDenied && p.tickmillProductId === TickmillProductType.ETD)
      ),
    [activeProducts, isFuturesDenied]
  )

  const handleChangeLandingPage = (landingPageId: number) => {
    setFieldValue('landingPageId', landingPageId)
    handleSubmit()
  }

  const dropdownOptions = useMemo(() => {
    return selectableProducts.flatMap((p) => p.landingPages)
  }, [selectableProducts])

  return (
    <Box flex gap='x24' direction='column'>
      <Body1>{t('Profile.Choose which page welcomes you every time you log in to Tickmill')}</Body1>
      <PopoverInput
        name='landingPageId'
        label={t('Profile.Landing Page')}
        disabled={!landingPageOptions.length || !activeProducts.length}
        value={values.landingPageId}
        options={dropdownOptions}
        onChange={(landingPage) => handleChangeLandingPage(landingPage.id)}
      />
    </Box>
  )
}

export const LandingPageForm = withFormik<OuterProps, LandingPageFormValues>({
  mapPropsToValues: ({ landingPage }) => {
    return {
      landingPageId: landingPage,
    }
  },
  validate: () => {},
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
})(LandingPageFormUI)
